<template>
  <div class="min-h-screen">
    <Header />
    
    <!-- Hero Section -->
    <section class="page-hero-shell pt-32 pb-20">
      <div class="absolute inset-0 page-overlay-layer space-pattern opacity-45"></div>
      <div class="hero-orb page-overlay-layer hero-orb-b"></div>
      <div class="hero-orb page-overlay-layer hero-orb-c"></div>
      
      <div class="page-content-layer max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <div class="hero-badge mb-6">
            <span>行业方案库</span>
          </div>
          <h1 class="text-5xl md:text-7xl font-bold mb-6">
            <span class="gradient-text">解决方案</span>
          </h1>
          <p class="text-xl text-gray-700 dark:text-dark-muted max-w-3xl mx-auto">
            针对不同行业和业务场景，提供定制化的数字营销解决方案
          </p>
        </div>
      </div>
    </section>

    <!-- Industry Solutions -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="section-title gradient-text mb-16">行业解决方案</h2>
        
        <div class="space-y-16 p-6 md:p-8">
          <div v-for="(solution, index) in solutions" :key="index"
               class="glass-card p-8 md:p-12 border border-white/10 visual-surface"
               :class="{ 'flex-row-reverse': index % 2 === 1 }">
            <div class="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div class="flex items-center mb-6">
                  <div class="w-16 h-16 icon-shell icon-shell-strong mr-4">
                    <FeatureIcon :name="solution.iconName || 'targeting'" size="xl" variant="minimal" />
                  </div>
                  <div>
                    <h3 class="text-2xl font-bold text-white">{{ solution.title }}</h3>
                    <p class="text-cosmic-adaptive">{{ solution.subtitle }}</p>
                  </div>
                </div>
                
                <p class="text-gray-700 dark:text-dark-muted mb-6">{{ solution.description }}</p>
                
                <div class="space-y-4 mb-6">
                  <div v-for="challenge in solution.challenges" :key="challenge" 
                       class="flex items-start">
                    <svg class="w-5 h-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                    </svg>
                    <span class="text-gray-700 dark:text-dark-muted">{{ challenge }}</span>
                  </div>
                </div>
                
                <div class="space-y-3">
                  <h4 class="text-white font-semibold">我们的解决方案：</h4>
                  <div v-for="item in solution.solutions" :key="item" 
                       class="flex items-start">
                    <svg class="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                    </svg>
                    <span class="text-gray-700 dark:text-dark-muted">{{ item }}</span>
                  </div>
                </div>
              </div>
              
            <div class="glass-card p-6 visual-surface solution-results-surface">
                <h4 class="text-white font-bold mb-4">核心成果</h4>
                <div class="space-y-4">
                  <div v-for="(result, idx) in solution.results" :key="idx"
                       class="rounded-lg p-4 solution-results-item">
                    <div class="flex justify-between items-center mb-2">
                      <span class="text-gray-700 dark:text-dark-muted">{{ result.label }}</span>
                      <span class="text-xl font-bold gradient-text">{{ result.value }}</span>
                    </div>
                    <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 transition-colors duration-200">
                      <div class="bg-gradient-to-r from-cosmic to-nebula h-2 rounded-full" 
                           :style="{ width: result.percentage }"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases -->
    <section class="py-20 relative">
      <div class="absolute inset-0 page-overlay-layer bg-gradient-to-r from-cosmic/5 via-nebula/5 to-stellar/5"></div>
      
      <div class="page-content-layer max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="section-title gradient-text mb-16">应用场景</h2>
        
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="useCase in useCases" :key="useCase.id"
               class="card card-hover visual-surface">
            <div class="w-16 h-16 mx-auto mb-6 icon-shell">
              <FeatureIcon :name="useCase.iconName || 'targeting'" size="xl" variant="minimal" />
            </div>
            <h3 class="text-xl font-bold mb-4 text-white text-center">{{ useCase.title }}</h3>
            <p class="text-gray-700 dark:text-dark-muted mb-6 text-center">{{ useCase.description }}</p>

            <div class="space-y-2">
              <div v-for="feature in useCase.features" :key="feature"
                       class="flex items-center text-sm text-gray-700 dark:text-dark-muted">
                <svg class="w-4 h-4 mr-2 text-cosmic" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                {{ feature }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Process -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="section-title gradient-text mb-16">合作流程</h2>
        
        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          <!-- 添加连接线条 -->
          <div class="hidden lg:block absolute top-10 left-0 right-0 h-0.5 bg-gradient-to-r from-cosmic via-nebula to-stellar" 
               style="transform: translateY(-50%); z-index: 0;"></div>
          
          <div v-for="(step, index) in steps" :key="index"
               class="text-center relative z-10">
            <div class="relative mb-6">
              <div class="w-20 h-20 rounded-full icon-shell icon-shell-strong mx-auto">
                <FeatureIcon :name="step.iconName || 'rocket'" size="xl" variant="minimal" />
              </div>
            </div>

            <h3 class="text-xl font-bold mb-3 text-dark-text">{{ step.title }}</h3>
            <p class="text-gray-700 dark:text-dark-muted text-sm">{{ step.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-32">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-4xl md:text-5xl font-bold mb-6 gradient-text">获取专属解决方案</h2>
        <p class="text-xl text-gray-700 dark:text-dark-muted mb-12">
          我们的专业团队将根据您的业务需求，制定最适合的解决方案
        </p>
        
        <div class="flex flex-col sm:flex-row gap-6 justify-center">
          <button @click="scrollToAboutContact" class="btn-primary">预约咨询</button>
          <button @click="scrollToAboutContact" class="btn-secondary">联系销售</button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
const solutions = [
  {
    icon: '🎮',
    iconName: 'gaming',
    title: '游戏行业',
    subtitle: '游戏获客与变现解决方案',
    description: '为游戏公司提供从用户获取到流量变现的全链路解决方案，帮助游戏企业实现用户增长和收益最大化。',
    challenges: [
      '用户获取成本高，质量难保证',
      '用户留存率低，生命周期短',
      '广告变现效率低下，收益不佳'
    ],
    solutions: [
      '基于DSP的DMP数据能力，精准定位目标玩家',
      '智能投放算法，提升用户质量和留存率',
      'SSP平台优化广告位配置，提升变现效率'
    ],
    results: [
      { label: '获客成本降低', value: '-45%', percentage: '55%' },
      { label: '用户留存提升', value: '+120%', percentage: '80%' },
      { label: '变现收益提升', value: '+180%', percentage: '90%' }
    ]
  },
  {
    icon: '🛒',
    iconName: 'shopping',
    title: '电商行业',
    subtitle: '电商营销转化解决方案',
    description: '为电商平台提供精准营销和转化优化解决方案，帮助电商企业提升转化率和ROI。',
    challenges: [
      '流量成本高，转化率低',
      '用户复购率低，LTV不高',
      '营销效果难衡量，优化困难'
    ],
    solutions: [
      '精准用户定向，提升广告投放效果',
      '私域流量运营，提升用户复购率',
      '数据分析平台，全面监控营销效果'
    ],
    results: [
      { label: '转化率提升', value: '+180%', percentage: '90%' },
      { label: '复购率提升', value: '+150%', percentage: '75%' },
      { label: 'ROI提升', value: '+320%', percentage: '95%' }
    ]
  },
  {
    icon: '🏦',
    iconName: 'finance',
    title: '金融行业',
    subtitle: '金融获客与风控解决方案',
    description: '为金融机构提供合规的获客和风控解决方案，帮助金融企业实现精准获客和风险控制。',
    challenges: [
      '获客渠道单一，成本高昂',
      '用户质量参差不齐，风险高',
      '合规要求严格，投放受限'
    ],
    solutions: [
      '多渠道精准获客，降低获客成本',
      '智能风控模型，提升用户质量',
      '合规投放方案，确保监管合规'
    ],
    results: [
      { label: '获客成本降低', value: '-50%', percentage: '50%' },
      { label: '用户质量提升', value: '+80%', percentage: '80%' },
      { label: '通过率提升', value: '+60%', percentage: '60%' }
    ]
  }
]

const useCases = [
  {
    icon: '📱',
    iconName: 'mobile',
    title: '用户获取',
    description: '通过精准定向和智能投放，高效获取高质量用户',
    features: ['精准定向', '智能投放', '实时优化']
  },
  {
    icon: '💰',
    iconName: 'money',
    title: '流量变现',
    description: '优化广告位配置，最大化媒体流量价值',
    features: ['收益优化', '实时竞价', '多渠道管理']
  },
  {
    icon: '👥',
    iconName: 'users',
    title: '私域运营',
    description: '构建私域流量池，提升用户生命周期价值',
    features: ['用户沉淀', '精准营销', '会员体系']
  },
  {
    icon: '🌍',
    iconName: 'global',
    title: '出海营销',
    description: '跨境广告投放，快速拓展海外市场',
    features: ['全球覆盖', '本地化', '合规保障']
  },
  {
    icon: '📊',
    iconName: 'data',
    title: '数据分析',
    description: '深度数据分析，提供营销决策支持',
    features: ['用户画像', '行为分析', '效果预测']
  },
  {
    icon: '🔄',
    iconName: 'lifecycle',
    title: '全链路营销',
    description: '从获客到转化的全链路营销解决方案',
    features: ['全渠道覆盖', '统一管理', '效果追踪']
  }
]

const steps = [
  {
    icon: '📞',
    iconName: 'phone',
    title: '需求沟通',
    description: '了解您的业务需求和目标'
  },
  {
    icon: '📋',
    iconName: 'document',
    title: '方案制定',
    description: '制定专属的解决方案'
  },
  {
    icon: '🚀',
    iconName: 'integration',
    title: '实施上线',
    description: '快速部署和上线'
  },
  {
    icon: '📈',
    iconName: 'growth',
    title: '持续优化',
    description: '持续监控和优化效果'
  }
]

const scrollToAboutContact = () => {
  window.location.href = '/about#contact'
}

</script>
